import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs/operators';
import { NavigationItem } from '@app/core/types/navigation.interface';
import { AlertService } from '@app/shared/services';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe, MatIconModule],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly alertService = inject(AlertService);
  private readonly route = inject(ActivatedRoute);
  type = input<string>('theme1');
  isComponent = input(false);
  dashboard = input(true);
  readonly alert = toSignal(this.alertService.getAlerts().pipe(map((alerts) => alerts[0])));

  private readonly navigationEvents = toSignal(
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
  );

  readonly breadcrumbList = computed(() => {
    this.navigationEvents();
    const crumbs = this.createBreadcrumbs(this.route.root);
    const homeItem: NavigationItem = { id: 'Inicio', title: 'Inicio', type: 'item', url: '/home' };
    const isAlreadyHomePage = crumbs.length > 0 && crumbs[0]?.id === 'Inicio';
    if (!isAlreadyHomePage) {
      crumbs.unshift(homeItem);
    }
    return this.formatBreadcrumbs(crumbs);
  });

  constructor() {
    effect(() => {
      const crumbs = this.breadcrumbList();
      const pageTitle = crumbs.length ? crumbs[crumbs.length - 1].title : 'Bienvenido';
      this.titleService.setTitle(`${pageTitle} | Bind ERP`);
    });
  }

  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: NavigationItem[] = []): NavigationItem[] {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      let newUrl = url;
      if (routeURL !== '') {
        newUrl += `/${routeURL}`;
      }

      const breadcrumbLabel = child.snapshot.data['breadcrumb'] as string;
      if (breadcrumbLabel) {
        breadcrumbs.push({ id: breadcrumbLabel, title: breadcrumbLabel, type: 'item', url: newUrl });
      }

      return this.createBreadcrumbs(child, newUrl, breadcrumbs);
    }
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatBreadcrumbs(items: NavigationItem[]): any[] {
     return items
       .filter(item => !!item.title)
       .map((item, idx) => ({
         key: idx,
         title: item.title,
         url: (typeof item.url === 'string' && item.url) || false,
         type: 'item',
         breadcrumbs: true
       }));
  }

  readonly alertCssClass = computed(() => {
       const alertType = this.alert()?.type;
    if (!alertType) return 'status-alert--info';

    switch (alertType) {
      case 'success': return 'status-alert--success';
      case 'warning': return 'status-alert--warning';
      case 'error': return 'status-alert--danger';
      default: return 'status-alert--info';
    }
  });
}
